export type PaymentMethodId =
	| "card"
	| "apple-pay"
	| "google-pay"
	| "revolut-pay"
	| "sepa"
	| "paypal"
	| "robinhood-connect"
	| "banxa"
	| "moonpay"
	| "coinbase-pay"
	| "binance";

export type Provider = {
	id: string;
	name: string;
	minAmountUSD: number;
	minAmountEUR: number;
	paymentMethods: PaymentMethodId[];
	channels: string[];
	enabled: boolean;
};

export const PROVIDERS: Provider[] = [
	{
		id: "wert",
		name: "Wert",
		minAmountUSD: 1,
		minAmountEUR: 1,
		paymentMethods: ["card", "apple-pay", "google-pay"],
		channels: ["eu"],
		enabled: false,
	},
	{
		id: "stripe",
		name: "Stripe",
		minAmountUSD: 1,
		minAmountEUR: 1,
		paymentMethods: ["card", "apple-pay", "google-pay"],
		channels: ["us"],
		enabled: true,
	},
	{
		id: "revolut",
		name: "Revolut",
		minAmountUSD: 6,
		minAmountEUR: 6,
		paymentMethods: ["revolut-pay"],
		channels: ["eu"],
		enabled: true,
	},
	{
		id: "cryptocom",
		name: "Crypto.com",
		minAmountUSD: 5,
		minAmountEUR: 5,
		paymentMethods: ["card"],
		channels: ["eu"],
		enabled: false,
	},
	{
		// PayGate provider-status min raised to $30 (verified live 2026-07-09).
		// minAmountEUR ~= $30 at current FX so sub-minimum carts never reach
		// PayGate's own rejection.
		id: "moonpay",
		name: "MoonPay",
		minAmountUSD: 30,
		minAmountEUR: 26,
		// 2026-07-09: PayGate routes provider=moonpay to a Coindisco-hosted flow;
		// offered only as an explicit standalone option. Card-type methods moved
		// to Banxa, PayPal removed (no on-ramp currently supports it here).
		paymentMethods: ["moonpay"],
		channels: ["eu", "us"],
		enabled: true,
	},
	{
		// Re-enabled 2026-07-09 (provider-status: active, min $20; redirect to
		// ledger.banxa.com verified).
		id: "banxa",
		name: "Banxa",
		minAmountUSD: 20,
		minAmountEUR: 18,
		paymentMethods: ["banxa", "card", "apple-pay", "google-pay", "sepa"],
		channels: ["eu", "us"],
		enabled: true,
	},
	{
		// Coinbase Pay via PayGate (provider id "coinbase", verified live
		// 2026-07-09: status active, min $2; redirects to the Meld.io-hosted
		// Coinbase flow).
		id: "coinbase",
		name: "Coinbase Pay",
		minAmountUSD: 2,
		minAmountEUR: 2,
		paymentMethods: ["coinbase-pay"],
		channels: ["eu", "us"],
		enabled: true,
	},
	{
		// Binance Connect — EU-only effectively (PayGate detects customer
		// geo at redirect time; non-EU IPs cause the upstream Binance API
		// call to fail). We list it on the eu channel only.
		id: "binance",
		name: "Binance Connect",
		minAmountUSD: 15,
		minAmountEUR: 15,
		paymentMethods: ["binance"],
		channels: ["eu"],
		enabled: true,
	},
	{
		id: "robinhood",
		name: "Robinhood",
		minAmountUSD: 1,
		minAmountEUR: 1,
		paymentMethods: ["robinhood-connect"],
		channels: ["us"],
		enabled: true,
	},
];

type FilterParams = {
	orderTotal: number;
	currency: string;
	channel: string;
};

export function getAvailableProviders({ orderTotal, currency, channel }: FilterParams): Provider[] {
	return PROVIDERS.filter((provider) => {
		if (!provider.enabled) return false;
		if (!provider.channels.includes(channel)) return false;
		const minAmount = currency === "EUR" ? provider.minAmountEUR : provider.minAmountUSD;
		return orderTotal >= minAmount;
	});
}

/**
 * On-ramps that price the customer charge in the order's fiat currency (EUR).
 * Everything else is USD-priced: PayGate settles USDC (~USD) and these providers
 * treat the `amount` as USD, so we convert EUR→USD before charging them. Keep in
 * sync with PayGate provider-status `minimum_currency` (only Revolut is EUR today).
 */
const EUR_NATIVE_PROVIDERS = new Set<string>(["revolut"]);

/**
 * Laender, in denen PayGate Revolut NICHT anbietet, obwohl der Kanal `eu` ist.
 *
 * Gemessen am 2026-08-19 gegen die hosted Zahlseite (identische URL, nur der Header
 * `PGTO-IPCountry` variiert): Revolut rendert in 15 von 20 EU-Locale-Laendern, aber
 * nicht in diesen fuenf. NL bekommt stattdessen iDEAL. Ohne dieses Gate zeigt die
 * Direkt-Kachel eine Zahlart, die PayGate fuer das Land nicht fuehrt.
 *
 * Bewusste Deny-List statt Allow-List: faellt die Messung kuenftig anders aus, ist
 * der Fehlerfall "Kachel wird gezeigt, obwohl sie fehlt" (wie heute) und nicht
 * "Kachel fehlt in einem Land, wo sie funktioniert".
 *
 * Grenze der Methode: PayGate entscheidet nach der IP (`PGTO-IPCountry`), wir gaten
 * nach der Checkout-Adresse. Ein Kaeufer mit abweichender IP faellt weiterhin durch
 * das Raster - dann greift die hosted Kachel, die PayGate selbst korrekt kuratiert.
 */
const REVOLUT_UNSUPPORTED_COUNTRIES = new Set<string>(["NL", "HU", "BG", "HR", "TR"]);

export function isRevolutAvailableForCountry(countryCode: string | null | undefined): boolean {
	if (!countryCode) return true;
	return !REVOLUT_UNSUPPORTED_COUNTRIES.has(countryCode.trim().toUpperCase());
}

export function providerChargesInUsd(providerId: string): boolean {
	return !EUR_NATIVE_PROVIDERS.has(providerId);
}
